<template>
  <nav
    class="site-nav"
    :class="{ fixed: !mobile, 'sidebar-collapsed': sidebarCollapsed }"
  >
    <div class="nav-links flex items-center gap-3">
      <button
        v-if="mobile"
        type="button"
        @click="emits('toggle-menu')"
      >
        <Icon
          data="@/assets/icons/hamburger.svg"
          class="icon"
          :fill="false"
        />
      </button>
      <NuxtLink
        v-if="!props.showBreadcrumbs"
        to="/"
      >
        <Icon
          :data="appLogoCropped"
          original
          height="40"
          width="auto"
        />
      </NuxtLink>
      <v-breadcrumbs
        v-else
        class="breadcrumbs text-foreground"
        :items="matchedRoutes"
        divider=">"
      >
        <template #item="slotData">
          <NuxtLink
            v-if="!slotData.item.exact"
            class="breadcrumb-link"
            :to="slotData.item.href ?? '#'"
          >
            {{ slotData.item.title }}
          </NuxtLink>
          <a
            v-else
            class="breadcrumb-link router-link-exact-active"
            :href="slotData.item.href ?? '#'"
          >
            {{ slotData.item.title }}
          </a>
        </template>
      </v-breadcrumbs>
    </div>
    <div class="shortcuts flex items-center gap-3">
      <div
        v-if="true"
        class="theme-selector"
      >
        <v-btn-toggle
          v-model="theme"
          background-color="transparent"
          class="gap-2"
        >
          <v-btn
            class="theme-btn"
            :class="{ active: theme === 'light', 'text-primary': true }"
            variant="plain"
            @click="theme = 'light'"
          >
            <Icon
              data="@/assets/icons/light_theme.svg"
              class="icon"
            />
          </v-btn>

          <v-btn
            class="theme-btn"
            :class="{ active: theme === 'dark', 'text-primary': true }"
            variant="plain"
            @click="theme = 'dark'"
          >
            <Icon
              data="@/assets/icons/dark_theme.svg"
              class="icon"
            />
          </v-btn>
        </v-btn-toggle>
      </div>
      <v-badge
        color="primary"
        :content="unreadNotificationsCount"
      >
        <button
          class="notification-btn"
          @click="showNotifications = !showNotifications"
        >
          <Icon
            data="@/assets/icons/bell.svg"
            class="icon"
            :fill="false"
          />
        </button>
      </v-badge>
      <div
        class="flex items-center gap-3 rounded-full py-1 pl-1 pr-3 text-app-slate-900"
      >
        <button
          class="flex items-center gap-1"
          @click="navigateTo({ name: 'profile' })"
        >
          <v-avatar
            color="var(--app-gray-103)"
            class="text-foreground"
            :image="userProfile?.image?.url"
            :text="
              getInitials(
                `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`,
              )
            "
          />
          <span v-if="!mobile">{{
            capitalize(
              `${userData?.firstName ?? ""} ${userData?.lastName ?? ""}`,
            )
          }}</span>
        </button>
        <CustomPopper
          placement="bottom-end"
          :offset-distance="24"
          @click.stop
        >
          <button>
            <v-icon color="var(--app-slate-403)"> mdi-chevron-down </v-icon>
          </button>
          <template #content>
            <ProfileNavDropdown />
          </template>
        </CustomPopper>
      </div>
      <button
        v-if="showLogout"
        type="button"
        class="flex flex-nowrap items-center gap-2"
        @click="emits('logout')"
      >
        <Icon
          data="@/assets/icons/exit.svg"
          class="icon"
          original
          :fill="false"
          width="24"
          height="auto"
        />
      </button>
      <!-- <Dropdown>
        <Icon
          class="icon cursor-pointer"
          data="@/assets/icons/chevron_down.svg"
          original
          height="20"
        />
        <template #content> </template>
      </Dropdown> -->
      <LazyNotificationList
        v-if="showNotifications"
        ref="notifcationListRef"
        @close="showNotifications = false"
        class="notifications-list !top-[5rem] !right-10"
      />
    </div>
  </nav>
</template>

<script setup lang="ts">
import capitalize from "lodash-es/capitalize";
import type { VBreadcrumbs } from "vuetify/components";
import { onClickOutside } from "@vueuse/core";
import { useDisplay } from "vuetify";
import type { ComponentPublicInstance } from "vue";
import ProfileNavDropdown from "./ProfileNavDropdown.vue";
import type { UserInfo } from "~/types/entities/auth";
import { CustomTitleSymbol } from "~/types/symbols";
import { getInitials } from "~/utils";
import { appLogoCropped } from "~/composables/app";

interface Profile extends UserInfo {
  image: {
    url: string;
    fileId: string;
  } | null;
}
interface Props {
  showBreadcrumbs?: boolean;
  showLogout?: boolean;
}
interface Emits {
  (e: "logout" | "toggle-menu"): void;
}

const LazyNotificationList = defineAsyncComponent(
  () => import("~/components/Notification/List.vue"),
);
const props = defineProps<Props>();
const emits = defineEmits<Emits>();

const { mobile } = useDisplay();
const { collapsed: sidebarCollapsed } = useSidebarStore();
const notifcationListRef = ref<ComponentPublicInstance>();
onClickOutside(notifcationListRef, (event) => {
  if ((event.target as HTMLDivElement)?.closest(".v-overlay")) return;
  showNotifications.value = false;
});

const notificationStore = useNotificationStore();
const sessionStore = useSessionStore();
await isModuleReady(notificationStore);
await isModuleReady(sessionStore);
const { unreadNotificationsCount } = storeToRefs(notificationStore);
const { userData } = storeToRefs(sessionStore);
const route = useRoute();
const router = useRouter();
const showNotifications = ref(false);

const { theme } = useTheme();
const customRouteTitle = inject(CustomTitleSymbol);
const matchedRoutes = computed<VBreadcrumbs["$props"]["items"]>(() =>
  router
    .getRoutes()
    .filter((Iroute) => {
      return (
        Iroute.path !== "/" &&
        (route.name as string)?.includes(
          router.resolve(Iroute.path).name as string,
        )
      );
    })
    .map<NonNullable<VBreadcrumbs["$props"]["items"]>[number]>(
      (matchedRoute) => {
        const displayName = route.query.displayName || customRouteTitle?.value;
        const type = route.query.type;
        const isCurrentRoute =
          route.name === router.resolve(matchedRoute.path).name;
        const routeHasParams =
          route.params && Object.keys(route.params).length > 0;
        // console.log(route.params, 'rhh');
        // console.log(routeHasParams,'routeHasParams');
        const routeName = !isCurrentRoute
          ? matchedRoute.name
          : routeHasParams
            ? displayName ||
              `${Object.keys(route.params ?? {})
                .filter((key, index) => index < 1)
                .map((key) => (route.params as Record<string, string>)?.[key])
                .join("-")}`
            : displayName || matchedRoute.name;

        return {
          title: capitalize(
            String(routeName ?? "")
              .split("-")
              .join(" "),
          ),
          href: isCurrentRoute
            ? "#"
            : type
              ? `${matchedRoute.path}?type=${type}`
              : matchedRoute.path,
          disabled: false,
          exact:
            matchedRoute.path === route.path ||
            route.name === router.resolve(matchedRoute.path).name,
        };
      },
    )
    .reverse(),
);
const userProfile = asyncComputed<Profile | undefined>(async () => {
  if (!sessionStore.isLoggedIn) return;
  try {
    const resp = await useRequest<{
      data: Profile;
    }>("users");
    return resp?._data?.data;
  } catch (error) {
    return;
  }
});
</script>

<style scoped lang="scss">
@reference "#tailwind.css";
nav.site-nav {
  @apply left-4 right-4 md:right-6 flex z-20 items-center px-5 py-3 bg-surface text-foreground font-bold rounded-3xl;
  top: 1rem;
  box-shadow: 0px 10px 25px var(--app-gray-401-alpha-4);
  transition: left 0.22s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    left: 248px; // sidebar 224px + left gap 12px + nav gap 12px
  }

  &.sidebar-collapsed {
    @media (min-width: 768px) {
      left: 96px; // collapsed sidebar 72px + left gap 12px + nav gap 12px
    }
  }

  .shortcuts {
    @apply ml-auto;
    min-height: 48px;
  }

  .theme-selector {
    @apply rounded-[200px] bg-app-gray-50 border border-solid border-app-gray-200 py-1 px-1;

    :deep(.v-btn-group--density-default.v-btn-group) {
      height: 40px;
    }

    .theme-btn {
      @apply max-h-[40px] max-w-[40px] min-w-[40px];
      border-radius: 50% !important;

      &.active {
        @apply bg-surface text-primary;
      }
    }
  }

  .notification-btn {
    @apply flex h-11 w-11 justify-center items-center rounded-full bg-primary-100 text-primary;
  }

  .profile-trigger {
    @apply flex items-center gap-3 rounded-full bg-surface py-1 pl-1 pr-3 text-app-gray-900;

    span {
      @apply text-sm font-semibold;
    }
  }

  .breadcrumbs {
    .breadcrumb-link {
      &.router-link-exact-active {
        @apply text-primary;
      }
    }
  }
  .notifications-list {
    :deep(.virtual-scroller) {
      height: calc(100dvh - 100px);
      :deep(.vue-recycle-scroller__item-wrapper) {
        height: 100%;
      }
    }
  }
}
</style>
